"use client";

import { useState } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  useDroppable,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { ContentBlock, ContentBlockType, create_default_block, add_blocks_metadata } from '@/features/editor/utils/content-blocks';
import { ContentRenderer } from '@/features/content/components/ContentRenderer';
import { BlockEditor } from './BlockEditor';
import { BlockPalette } from './BlockPalette';
import { SortableContentBlock } from './SortableContentBlock';
import { ContentDropZone } from './ContentDropZone';
import { Banner } from "@/components/shared/banner";  
import ShareAndAuthorInfo from '@/features/marketing/components/blog/ShareAndAuthorInfo';

interface ContentEditorProps {
  content: ContentBlock[];
  onChange: (blocks: ContentBlock[]) => void;
  contentMetadata?: {
    title: string;
    description: string;
    author: { name: string; image: string };
    publishDate: string;
    readingTime: string;
    hero: { image: string; imageAlt: string };
  };
  onMetadataChange?: (metadata: any) => void;
}

export function ContentEditor({ content, onChange, contentMetadata, onMetadataChange }: ContentEditorProps) {
  const [activeId, setActiveId] = useState<string | null>(null);
  const [draggedBlockType, setDraggedBlockType] = useState<ContentBlockType | null>(null);
  const [editingBanner, setEditingBanner] = useState(false);
  const [editingFooter, setEditingFooter] = useState(false);
  
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const addBlock = (type: ContentBlockType) => {
    const newBlock = create_default_block(type);
    onChange([...content, newBlock]);
  };

  const updateBlock = (id: string, updatedBlock: ContentBlock) => {
    const index = parseInt(id.replace('block-', ''));
    if (Number.isFinite(index) && index >= 0 && index < content.length) {
      const newBlocks = [...content];
      newBlocks[index] = updatedBlock;
      onChange(newBlocks);
    }
  };

  const deleteBlock = (id: string) => {
    const index = parseInt(id.replace('block-', ''));
    if (Number.isFinite(index) && index >= 0 && index < content.length) {
      const newBlocks = [...content];
      newBlocks.splice(index, 1);
      onChange(newBlocks);
    }
  };

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveId(active.id as string);
    
    // Check if dragging from palette
    if (active.id.toString().startsWith('palette-')) {
      const blockType = active.id.toString().replace('palette-', '') as ContentBlockType;
      setDraggedBlockType(blockType);
    }
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    
    // If dragging from palette and over a content area
    if (active.id.toString().startsWith('palette-') && over && over.id === 'content-area') {
      // This will be handled in handleDragEnd
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveId(null);
    setDraggedBlockType(null);

    if (!over) return;

    // Handle dragging from palette to content area
    if (active.id.toString().startsWith('palette-') && over.id === 'content-area') {
      const blockType = active.id.toString().replace('palette-', '') as ContentBlockType;
      const newBlock = create_default_block(blockType);
      onChange([...content, newBlock]);
      return;
    }

    // Handle dragging from palette to specific position
    if (active.id.toString().startsWith('palette-') && over.id.toString().startsWith('content-')) {
      const blockType = active.id.toString().replace('palette-', '') as ContentBlockType;
      const targetIndex = parseInt(over.id.toString().replace('content-', ''));
      
      const newBlock = create_default_block(blockType);
      const newBlocks = [...content];
      newBlocks.splice(targetIndex, 0, newBlock);
      onChange(newBlocks);
      return;
    }

    // Handle reordering existing blocks
    if (active.id !== over.id && !active.id.toString().startsWith('palette-')) {
      const oldIndex = parseInt(active.id.toString().replace('block-', ''));
      const newIndex = parseInt(over.id.toString().replace('block-', ''));

      if (Number.isFinite(oldIndex) && Number.isFinite(newIndex)) {
        const newBlocks = arrayMove(content, oldIndex, newIndex);
        onChange(newBlocks);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full px-6 py-6">
        
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-6">
            {/* Block Palette */}
            <div className="w-72 bg-white/70 backdrop-blur p-4 border border-gray-200 rounded-xl shadow-sm sticky top-24 h-fit">
              <BlockPalette onAddBlock={addBlock} />
            </div>

            {/* Content Area */}
            <div className="flex-1">
              {/* Editable Banner */}
              {contentMetadata && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-blue-600 font-medium">🎯 Banner</span>
                      <span className="text-xs text-blue-500">Cannot be removed or repositioned</span>
                    </div>
                    <button
                      onClick={() => setEditingBanner(!editingBanner)}
                      className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                    >
                      {editingBanner ? 'Done' : 'Edit'}
                    </button>
                  </div>
                  
                  {editingBanner ? (
                    <div className="space-y-3 p-4 bg-white rounded border">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                        <input
                          type="text"
                          value={contentMetadata.title}
                          onChange={(e) => onMetadataChange?.({ ...contentMetadata, title: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={contentMetadata.description}
                          onChange={(e) => onMetadataChange?.({ ...contentMetadata, description: e.target.value })}
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          rows={3}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Author Name</label>
                          <input
                            type="text"
                            value={contentMetadata.author.name}
                            onChange={(e) => onMetadataChange?.({ ...contentMetadata, author: { ...contentMetadata.author, name: e.target.value } })}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Author Image</label>
                          <input
                            type="text"
                            value={contentMetadata.author.image}
                            onChange={(e) => onMetadataChange?.({ ...contentMetadata, author: { ...contentMetadata.author, image: e.target.value } })}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Publish Date</label>
                          <input
                            type="text"
                            value={contentMetadata.publishDate}
                            onChange={(e) => onMetadataChange?.({ ...contentMetadata, publishDate: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Reading Time</label>
                          <input
                            type="text"
                            value={contentMetadata.readingTime}
                            onChange={(e) => onMetadataChange?.({ ...contentMetadata, readingTime: e.target.value })}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Article Image</label>
                          <input
                            type="text"
                            value={contentMetadata.hero.image}
                            onChange={(e) => onMetadataChange?.({ ...contentMetadata, hero: { ...contentMetadata.hero, image: e.target.value } })}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Image Alt Text</label>
                          <input
                            type="text"
                            value={contentMetadata.hero.imageAlt}
                            onChange={(e) => onMetadataChange?.({ ...contentMetadata, hero: { ...contentMetadata.hero, imageAlt: e.target.value } })}
                            className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Banner
                        variant="article"
                        title={contentMetadata.title}
                        description={contentMetadata.description}
                        authorName={contentMetadata.author.name}
                        authorImage={contentMetadata.author.image}
                        publishDate={contentMetadata.publishDate}
                        readingTime={contentMetadata.readingTime}
                        articleImage={contentMetadata.hero.image}
                        articleImageAlt={contentMetadata.hero.imageAlt}
                      />
                    </div>
                  )}
                </div>
              )}

              <SortableContext
                items={content.map((_, idx) => `block-${idx}`)}
                strategy={verticalListSortingStrategy}
              >
                <ContentDropZone content={content} onUpdate={updateBlock} onDelete={deleteBlock} />
              </SortableContext>

              {/* Editable ShareAndAuthorInfo */}
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-green-600 font-medium">👤 Share & Author Info</span>
                    <span className="text-xs text-green-500">Cannot be removed or repositioned</span>
                  </div>
                  <button
                    onClick={() => setEditingFooter(!editingFooter)}
                    className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                  >
                    {editingFooter ? 'Done' : 'Edit'}
                  </button>
                </div>
                
                {editingFooter ? (
                  <div className="space-y-3 p-4 bg-white rounded border">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Author Image URL</label>
                      <input
                        type="text"
                        value="/team/ben.jpeg"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        placeholder="/team/jonathan.png"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Editorial Info</label>
                      <textarea
                        value="Ben Ward is the co-founder and CEO of StudyStash, an innovative EdTech platform that combines AI and neuroscience to revolutionise higher education. With a passion for personalized learning, Ben has led StudyStash to compete with global giants like Duolingo and Quizlet, making waves in the UK university sector."
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                        rows={4}
                        placeholder="About the author and additional information..."
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      This block includes social sharing buttons and author information.
                    </p>
                  </div>
                ) : (
                  <div>
                    <ShareAndAuthorInfo
                      authorImageUrl="/team/ben.jpeg"
                      editorialInfo="Ben Ward is the co-founder and CEO of StudyStash, an innovative EdTech platform that combines AI and neuroscience to revolutionise higher education. With a passion for personalized learning, Ben has led StudyStash to compete with global giants like Duolingo and Quizlet, making waves in the UK university sector."
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </DndContext>
      </div>
    </div>
  );
}

